import {message} from 'antd';
import {Position, RGBAColor} from 'deck.gl';
import {ConvertHexToRGBA, LegendColors,} from '../../utils/common';
import guid from '../../utils/Guid';
import {ILegend, overlay, surfaceMaps} from '../../types/home';
import {axiosInstance} from '../Api';
import InsituPoints from '../../mock/InSituPoints';
import {IPolygonLayer} from "../../types/map";
import {IUploadedLayer} from "../../types/legend";

export interface IDatasetZone {
  id: string;
  coordinates: Position[];
  color: RGBAColor;
}

export interface IFeature {
  type: string;
  properties: any;
  geometry: {
    type: string;
    coordinates: Position[][][] | Position[][];
  };
}

export interface IOverlay {
  data: IPolygonLayer[];
  legend: ILegend;
}

export interface ITenant {
  id: string;
  name: string
}

export interface IFolder {
  id: string;
  name: string
}

export interface IFWEPoint {
  position: Array<number>;
  tenant: ITenant;
  folder: IFolder;
  name: string;
  datasetId: string;
}

export interface IInsituPoint {
  position: Array<number>;
  source: string;
  name: string;
  startDate: string;
  endDate: string;
  type: string
  isExpanded?: boolean
  originalPosition?: Array<number>
}

export default class FileEndPoint {
  private endPoint = 'File';

  async getInSituPoints() {
    return InsituPoints
  }

  //get all mood space definition
  async getOverlayTree(datasetId: string) {
    const surfMaps = {
      selectedLevel1: '',
      selectedLevel2: '',
      categories: [],
      enable: false,
      overlays: [] as overlay[],
    } as surfaceMaps;
    try {
      const response = await axiosInstance.get(
        `/${this.endPoint}/GetOverlayTree?datasetId=${encodeURIComponent(datasetId)}`
      );
      if (response.data && response.data.Result) {
        const result = response.data.Result.value;

        if (result != null) {
          const index = [];
          for (const x in result) {
            index.push(x);
          }

          Object.keys(result).map((k) => {
            surfMaps.categories.push({ name: k, IsDisplay: false });
          });

          // Overrides the default value of LegendColor from backend
          // or adding LegendColor if not exist

          const legendColorsReverse = [...LegendColors].reverse();

          const models = Object.keys(result);
          for (let i = 0; i < index.length; i++) {
            for (let j = 0; j < result[index[i]].length; j++) {
              if (result[index[i]][j].LegendColor !== null) {
                result[index[i]][j].LegendColor = models[i]
                  .toLowerCase()
                  .includes('bathymetry')
                  ? LegendColors
                  : legendColorsReverse;
              } else {
                result[index[i]][j]['LegendColor'] = models[i]
                  .toLowerCase()
                  .includes('bathymetry')
                  ? LegendColors
                  : legendColorsReverse;
              }
            }
          }

          for (let i = 0; i < index.length; i++) {
            surfMaps.overlays = surfMaps.overlays.concat(
              result[index[i]] as overlay[]
            );
          }
        }
      }
    } catch (error: any) {
      message.error(error);
      console.log(error);
    }
    return surfMaps;
  }

  async getOverlayTree2(datasetId: string) {
    try {
      const r = await axiosInstance.get(
        `/${this.endPoint}/GetOverlayTree2?datasetId=${encodeURIComponent(datasetId)}`
      );
      if (r.data.Result === null) return [];
      else return r.data.Result!.value['Offshore Wind Farms'];
      // var index = [];
      // for (var x in r.data.Result!.Value.properties){
      //   index.push(x);
      // }

      // if(r.data.Result != null)
      //   return r.data.Result!.Value[index[0]];
    } catch (error: any) {
      message.error(error);
      console.log(error);
    }
    return [];
  }

  async uploadFile(
    fileType: string,
    fileNum: number,
    orderId: string,
    fileData: File[]
  ) {
    try {
      const formData = new FormData();

      for (const fileDataItem of fileData) {
        formData.append('files', fileDataItem);
      }

      const r = await axiosInstance.post(
        `/${this.endPoint}/Upload?fileType=${fileType}&fileNum=${fileNum}&orderId=${orderId}`,
        formData, {
          headers: {
            "Content-Type": "multipart/form-data"
          }
        }
      );

      if (r.data?.Error) {
        message.error(r.data.Error);
        return null;
      }

      return r.data?.Result ?? null;
    } catch (error: any) {
      message.error(error);
      console.log(error);
      return null;
    }
  }

  async checkFileStatus(orderId: string) {
    try {
      const r = await axiosInstance.get(
        `/${this.endPoint}/CheckFileStatus?guid=${orderId}`
      );
      return r?.data.Result;
    } catch (error: any) {
      message.error(error);
      console.log(error);
    }
  }

  async getGeoJsonLayer(orderId: string, color?: string): Promise<Record<'features', IUploadedLayer['features']> | null> {
    try {
      const r = await axiosInstance.get(
        `/${this.endPoint}/GetGeoJson?guid=${orderId}`
      );

      if (r.data?.Error) {
        console.error(r.data.Error);
        message.error(r.data.Error?.ErrorMessage, 5);
        return null;
      }

      return JSON.parse(r.data.Result);
    } catch (error: any) {
      message.error(error);
      return null;
    }
  }

  async getOverlayGeoJson(
    datasetId: string,
    level1: string,
    level2: string,
    color: string
  ) {
    const r = await axiosInstance.get(
      `/${this.endPoint}/GetOverlayGeoJson?datasetId=${encodeURIComponent(datasetId)}&level1=${level1}&level2=${level2}`
    );
    return this.convertGeoJson(r.data.Result, color);
  }

  private convertGeoJson(geoJsonString: string, color: string) {
    const shapeZones = new Array<IPolygonLayer>();
    try {
      const lineColor = ConvertHexToRGBA(color);
      const filledColor = ConvertHexToRGBA(color, 0.2);
      const result = JSON.parse(geoJsonString);
      (result.features as IFeature[]).forEach((feature) => {
        if (feature.geometry.type === 'MultiPolygon') {
          (feature.geometry.coordinates as Position[][][]).forEach(
            (polygon) => {
              const data : IPolygonLayer = {
                id: guid(),
                coordinates: polygon[0],
                lineColor: lineColor,
                color: filledColor,
              }

              if(feature.properties.NAME) {
                data.name = feature.properties.NAME as string
              }
              shapeZones.push(data);
            }
          );
        } else {
          (feature.geometry.coordinates as Position[][]).forEach((polygon) => {
            shapeZones.push({
              id: guid(),
              coordinates: polygon,
              lineColor: lineColor,
              color: filledColor,
            });
          });
        }
      })
    } catch (error: any) {
      message.error(error);
      console.log(error);
    }
    return shapeZones;
  }

  async getLayGeoJson(
    index: number,
    mapBound: number[],
    datasetId: string,
    overlay: overlay
  ) {
    try {
      const r = await axiosInstance.get(
        encodeURI(
          `/${this.endPoint}/GetLayerGeoJson?index=${index}&lonBegin=${mapBound[0]}&latBegin=${mapBound[1]}&lonEnd=${mapBound[2]}&latEnd=${mapBound[3]}&datasetId=${encodeURIComponent(datasetId)}&level1=${overlay.Level1}&level2=${overlay.Level2}`
        )
      );
      return r.data.Result;

    } catch (error: any) {
      message.error(error);
      console.log(error);
    }
  }
}
